/**
 * Форматтеры и словари статусов для экранов менеджера (проживания/номера).
 * Generic SaaS: только машинные ключи бэка → русские label'ы.
 */
import type { RoomStatus, StayState } from '@hsc/types';

/** Русские label'ы статусов номера (housekeeping wave 2026-06-18). */
export const ROOM_STATUS_LABEL: Readonly<Record<RoomStatus, string>> = {
  available: 'Свободен',
  occupied: 'Занят',
  pending_inspection: 'На проверке',
  cleaning: 'На уборке',
  repair: 'Ремонт',
  // maintenance — устаревший синоним repair (показываем как «Ремонт»).
  maintenance: 'Ремонт',
  out_of_order: 'Закрыт/OOO',
  seasonal_closed: 'Закрыт/OOO',
};

/** Тон бейджа статуса номера (CSS-модификатор). */
export const ROOM_STATUS_TONE: Readonly<Record<RoomStatus, string>> = {
  available: 'ok',
  occupied: 'busy',
  pending_inspection: 'info',
  cleaning: 'warn',
  repair: 'warn',
  maintenance: 'warn',
  out_of_order: 'danger',
  seasonal_closed: 'muted',
};

/**
 * Русские label'ы статусов проживания. Кроме StayState (доменный enum) бэк в
 * календаре отдаёт агрегированный `booked` (бронь без раздельных pending/
 * confirmed) — мапим и его, чтобы в списке/карточке не было сырого англ-кода.
 */
export const STAY_STATE_LABEL: Readonly<Record<StayState | 'booked', string>> = {
  pending: 'Ожидает',
  confirmed: 'Подтверждено',
  booked: 'Бронь',
  checked_in: 'Заехал',
  checked_out: 'Выехал',
  cancelled: 'Отменено',
  no_show: 'Не заехал',
};

export const STAY_STATE_TONE: Readonly<Record<StayState | 'booked', string>> = {
  pending: 'warn',
  confirmed: 'ok',
  booked: 'warn',
  checked_in: 'busy',
  checked_out: 'muted',
  cancelled: 'danger',
  no_show: 'danger',
};

/** Дата ISO → DD.MM.YYYY (локаль ru). Пустая строка при невалидной дате. */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * YYYY-MM-DD из Date по ЛОКАЛЬНОМУ календарю (год/месяц/день в часовом поясе
 * браузера), НЕ через toISOString() (тот переводит в UTC).
 *
 * Почему локально: все Date'ы у нас строятся на локальную полночь
 * (`new Date('2026-06-19T00:00:00')`, `new Date()` + `setHours(0,0,0,0)`),
 * то есть представляют конкретный календарный день, который видит менеджер.
 * `toISOString()` в поясах восточнее UTC (Москва +03) сдвигал бы такую
 * полночь на сутки назад (2026-06-19 00:00 МСК → 2026-06-18T21:00Z →
 * «2026-06-18»), из-за чего колонка шахматки с подписью 19 несла iso 18-го,
 * а бронь 19→20 красилась на 20-м (off-by-one). Локальное форматирование
 * держит iso консистентным с видимой подписью дня (#booking off-by-one).
 */
export function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Дата+время ISO → «ДД.ММ ЧЧ:ММ» (mono-колонка «Последняя уборка» §4.2).
 * Пустое/невалидное → undefined (вызывающий рисует em-dash «—»).
 */
export function formatDateTimeShort(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm} ${hh}:${mi}`;
}

/** Русский label статуса чистоты (overlay FSM). */
export const CLEANLINESS_LABEL: Readonly<Record<string, string>> = {
  clean: 'Чисто',
  dirty: 'Грязно',
  inspected: 'Осмотрен',
  out_of_order: 'Не в строю',
};

/** Копейки → «12 500 ₽» (минимум знаков). */
export function formatMoney(kopecks: number, currency: string): string {
  const major = kopecks / 100;
  const sym = currency === 'RUB' ? '₽' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
  return `${major.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${sym}`;
}

/** Русские label'ы удобств (amenities) типов номеров (i18n-гейт). */
export const AMENITY_LABEL: Readonly<Record<string, string>> = {
  wifi: 'Wi-Fi',
  tv: 'Телевизор',
  safe: 'Сейф',
  minibar: 'Мини-бар',
  air_conditioning: 'Кондиционер',
  balcony: 'Балкон',
  terrace: 'Терраса',
  sea_view: 'Вид на море',
  mountain_view: 'Вид на горы',
  bathrobe: 'Халат',
  coffee_machine: 'Кофемашина',
  kitchen: 'Кухня',
  jacuzzi: 'Джакузи',
  parking: 'Парковка',
  hairdryer: 'Фен',
  bath: 'Ванна',
  shower: 'Душ',
};

/** Список англ-кодов удобств → русские label'ы (неизвестные — как есть). */
export function amenityLabels(codes: readonly string[]): string[] {
  return codes.map((c) => AMENITY_LABEL[c] ?? c);
}

/**
 * URL фото типа номера из object_key. Боевой сид хранит абсолютный URL
 * внешнего CDN (Tilda) прямо в object_key — отдаём как есть; относительный
 * ключ (MinIO) → путь к медиа. Пустой ключ → undefined.
 */
export function photoUrl(objectKey: string | undefined | null): string | undefined {
  if (!objectKey) return undefined;
  if (/^https?:\/\//i.test(objectKey)) return objectKey;
  return `/v1/files/${objectKey.replace(/^\/+/, '')}`;
}
